import React, {useState, useRef, useLayoutEffect} from 'react';

export interface ProjectFile {
    name: string;    // Includes full path, e.g., "src/main.mo"
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
    // Fixed file system types
    fileSystem?: 'backend' | 'frontend' | 'asset';
    assetKey?: string;
    assetUrl?: string;
    displayIcon?: string;
    displayColor?: string;
    tooltipSuffix?: string;
    mimeType?: string;
    size?: number;
}

interface FileTreeNode {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: FileTreeNode[];
    originalFile?: ProjectFile;
    // Fixed file system types
    fileSystemType?: 'backend' | 'frontend' | 'asset';
}

interface FileTreeViewProps {
    node: FileTreeNode;
    level: number;
    onFileClick: (path: string) => void;
    onAssetClick?: (url: string) => void;
    // NEW: Add folder right-click handler
    onFolderRightClick?: (folderPath: string, event: React.MouseEvent) => void;
    activePath: string;
    activeType: 'backend' | 'frontend';
    showFileSystemIndicators?: boolean;
    containerWidth?: number;
}

// FIXED: Account for trash can space in truncation calculation
const calculateMaxChars = (level: number, containerWidth: number = 250, hasTrashCan: boolean = false): number => {
    const baseIndent = 8;
    const levelIndent = level * 16;
    const chevronSpace = 20;
    const iconSpace = 24;
    const trashCanSpace = hasTrashCan ? 28 : 0; // Account for trash can + margin
    const safetyBuffer = 20;

    const usedSpace = baseIndent + levelIndent + chevronSpace + iconSpace + trashCanSpace + safetyBuffer;
    const availableSpace = containerWidth - usedSpace;

    const charWidth = 9.5;
    const maxChars = Math.floor(availableSpace / charWidth);

    const conservativeMax = Math.max(maxChars - 1, 1);

    return conservativeMax;
};

const simpleTruncate = (text: string, maxChars: number): string => {
    // If we have very little space, be aggressive but allow a bit more
    if (maxChars <= 4) {
        return text.substring(0, Math.max(maxChars - 3, 1)) + '...';
    }

    // Special check for edge cases - if text is close to maxChars, be more aggressive
    if (text.length > maxChars - 1) { // Catch files that are very close to the limit
        return text.substring(0, maxChars - 3) + '...';
    }

    if (text.length <= maxChars) {
        return text;
    }

    // Simple truncation with ellipses
    return text.substring(0, maxChars - 3) + '...';
};


export const FileTreeView: React.FC<FileTreeViewProps> = ({
                                                              node,
                                                              level,
                                                              onFileClick,
                                                              onAssetClick,
                                                              onFolderRightClick,
                                                              activePath,
                                                              activeType,
                                                              showFileSystemIndicators = true,
                                                              containerWidth = 250
                                                          }) => {
    const [expanded, setExpanded] = useState(true);
    const textContainerRef = useRef<HTMLSpanElement>(null);
    const [displayName, setDisplayName] = useState(node.name);

    const indent = level * 16;

    if (!node) return null;

    const file = node.originalFile;
    const isAssetFile = file?.fileSystem === 'asset';
    const isFrontendFile = file?.fileSystem === 'frontend';
    const isBackendFile = file?.fileSystem === 'backend';

    // Calculate truncation on mount and when dependencies change
    useLayoutEffect(() => {
        const hasTrashCan = node.isDirectory && level > 0;
        const maxChars = calculateMaxChars(level, containerWidth, hasTrashCan);
        const truncated = simpleTruncate(node.name, maxChars);
        setDisplayName(truncated);
    }, [node.name, level, containerWidth, node.isDirectory]);

    // Force re-render by adding a key that changes
    const iconKey = `${node.name}-${file?.fileSystem || 'unknown'}`;



    const isFileVisibleForType = (node: FileTreeNode, activeType: 'frontend' | 'backend'): boolean => {
        if (node.isDirectory) {
            // For directories, always return true - let hasVisibleDescendants decide
            return true;
        }

        const file = node.originalFile;
        if (!file) return true;

        // Hide .keep files completely
        if (file.name.endsWith('/.keep') || file.fileName === '.keep') {
            return false;
        }

        // For virtual folders (asset folders)
        if (file.mimeType === 'directory' || file.mimeType === 'folder') {
            if (activeType === 'backend') {
                return file.fileSystem === 'backend';
            } else {
                return file.fileSystem === 'frontend' || file.fileSystem === 'asset';
            }
        }

        // File visibility logic
        if (activeType === 'backend') {
            return file.fileSystem === 'backend';
        } else {
            // Frontend mode: show frontend files AND assets
            return file.fileSystem === 'frontend' || file.fileSystem === 'asset';
        }
    };



    const isEmptyFolderThatShouldBeVisible = (node: FileTreeNode, activeType: 'frontend' | 'backend'): boolean => {
        if (!node.isDirectory) return false;

        // Check if this is truly empty or just has hidden .keep files
        const hasOnlyKeepFiles = node.children && node.children.length > 0 &&
            node.children.every(child => child.originalFile?.fileName === '.keep');

        const isTrulyEmpty = !node.children || node.children.length === 0;

        // FIXED: Special handling for virtual asset folders
        const file = node.originalFile;
        if (file && (file.mimeType === 'directory' || file.mimeType === 'folder')) {
            // This is a virtual folder - check if it should be visible for the active type
            if (activeType === 'frontend' && file.fileSystem === 'asset') {
                console.log('Found empty asset folder that should be visible:', {
                    name: node.name,
                    path: node.path,
                    fileSystem: file.fileSystem,
                    mimeType: file.mimeType
                });
                return true;
            }
            return isFileVisibleForType(node, activeType);
        }

        // For regular folders, show if empty/only has .keep files AND visible for current type
        return (isTrulyEmpty || hasOnlyKeepFiles) && isFileVisibleForType(node, activeType);
    };


    // FIXED: Replace your hasVisibleDescendants function with this smarter version

    const hasVisibleDescendants = (node: FileTreeNode, activeType: 'frontend' | 'backend'): boolean => {
        if (!node.children) return false;

        for (const child of node.children) {
            if (child.isDirectory) {
                // FIXED: Skip directories that don't match the active type
                // But still allow structural directories that contain matching content

                // Skip backend-specific directories when in frontend mode
                if (activeType === 'frontend' && child.name === 'backend') {
                    continue; // Skip the entire backend folder
                }

                // Skip frontend-specific directories when in backend mode
                if (activeType === 'backend' && child.name === 'frontend') {
                    continue; // Skip the entire frontend folder
                }

                // Recursively check descendants
                if (hasVisibleDescendants(child, activeType)) {
                    return true;
                }

                // Check if this directory should be visible as an empty folder
                if (isEmptyFolderThatShouldBeVisible(child, activeType)) {
                    return true;
                }
            } else {
                // Check if this file should be visible
                if (isFileVisibleForType(child, activeType)) {
                    return true;
                }
            }
        }
        return false;
    };

// ALSO UPDATE: The visibleChildren filtering to apply the same logic
    const visibleChildren = (node.children || []).filter(child => {
        // FIXED: Filter out backend/frontend folders based on active type
        if (child.isDirectory) {
            // Hide backend folder when in frontend mode
            if (activeType === 'frontend' && child.name === 'backend') {
                return false;
            }

            // Hide frontend folder when in backend mode
            if (activeType === 'backend' && child.name === 'frontend') {
                return false;
            }

            const hasVisibleChildren = hasVisibleDescendants(child, activeType);
            const shouldShowEmptyFolder = isEmptyFolderThatShouldBeVisible(child, activeType);
            return hasVisibleChildren || shouldShowEmptyFolder;
        } else {
            return isFileVisibleForType(child, activeType);
        }
    });


    if (node.isDirectory && visibleChildren.length === 0 && level > 0) {
        // Check if this directory should be shown as empty
        if (!isEmptyFolderThatShouldBeVisible(node, activeType)) {
            return null;
        }
    }

    // Don't render files that shouldn't be visible for current type
    if (!node.isDirectory && !isFileVisibleForType(node, activeType)) {
        return null;
    }

    // Determine styling based on file system
    const getNodeStyle = () => {
        const baseStyle = {
            display: 'flex',
            alignItems: 'center',
            padding: '2px 8px',
            paddingLeft: `${indent + 8}px`,
            cursor: 'pointer',
            borderRadius: '2px',
            margin: '1px 0',
            fontSize: '15px',
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
            letterSpacing: '0.2px',
            userSelect: 'none' as const,
            whiteSpace: 'nowrap' as const,
            overflow: 'hidden' as const,
            width: '100%',
            boxSizing: 'border-box' as const,
            minWidth: '0'
        };

        let backgroundColor = 'transparent';
        let color = '#383838';

        if (!node.isDirectory && node.path === activePath) {
            backgroundColor = 'rgba(139, 69, 19, 0.1)';
        }

        if (isAssetFile) {
            color = file?.displayColor || '#2196F3';
        } else if (isFrontendFile) {
            color = file?.displayColor || '#2196F3';
        } else if (isBackendFile) {
            color = file?.displayColor || '#5C4033';
        }

        if (node.name === 'README') {
            color = '#4A6FBF';
        }

        return {
            ...baseStyle,
            backgroundColor,
            color
        };
    };

    // Get appropriate icon based on file type
    const getIcon = () => {
        if (node.isDirectory) {
            return <i className="far fa-folder" style={{color: '#666'}}></i>;
        }

        // FIXED: Check if this is a virtual folder from our system
        if (file?.mimeType === 'directory' || file?.mimeType === 'folder') {
            return <i className="far fa-folder" style={{color: '#666'}}></i>;
        }

        // Get file extension first
        const fileName = node.name.toLowerCase();
        const extension = fileName.split('.').pop() || '';

        // Check for specific files first
        if (fileName === 'package.json') {
            return <i className="fas fa-cog" style={{color: '#FFA500', fontSize: '14px'}}></i>;
        }

        if (fileName.includes('readme')) {
            return <i className="fas fa-book-open" style={{color: '#4A6FBF', fontSize: '12px'}}></i>;
        }

        // Override custom display icons with our new logic for better consistency
        // Only use custom display icon for very specific cases
        if (file?.displayIcon && !['🌐', '💾', '📄', '📁'].includes(file.displayIcon)) {
            return <span style={{fontSize: '14px'}}>{file.displayIcon}</span>;
        }

        // Return appropriate icon based on file type
        switch (extension) {
            // JavaScript/TypeScript
            case 'js':
                return <i className="fab fa-js-square" style={{color: '#b69e22', fontSize: '16px'}}></i>;
            case 'jsx':
                return <i className="fab fa-react" style={{color: '#61DAFB', fontSize: '16px'}}></i>;
            case 'ts':
                return <span style={{color: '#3178C6', fontSize: '14px', fontWeight: 'bold', fontFamily: 'Arial'}}>TS</span>;
            case 'tsx':
                return <i className="fab fa-react" style={{color: '#3178C6', fontSize: '16px'}}></i>;

            // Web files
            case 'html':
                return <i className="fab fa-html5" style={{color: '#E34F26', fontSize: '16px'}}></i>;
            case 'css':
                return <i className="fab fa-css3-alt" style={{color: '#1572B6', fontSize: '16px'}}></i>;

            // Data/Config files
            case 'json':
                return <span style={{color: '#FFA500', fontSize: '14px', fontWeight: 'bold', fontFamily: 'monospace'}}>{`{}`}</span>;
            case 'toml':
                return <i className="fas fa-cog" style={{color: '#8B4513', fontSize: '14px'}}></i>;
            case 'yaml':
            case 'yml':
                return <i className="fas fa-file-lines" style={{color: '#FF6B6B', fontSize: '14px'}}></i>;

            // Motoko
            case 'mo':
                return <span style={{
                    fontSize: '25px',
                    fontWeight: 'bold',
                    background: 'linear-gradient(90deg, #a21caf, #6d28d9, #1d4ed8, #0e7490, #047857, #4d7c0f, #a16207, #c2410c, #b91c1c)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    WebkitTextFillColor: 'transparent',
                    position: 'relative',
                    top: '-2px'
                }}>∞</span>;

            // Images
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
            case 'webp':
                return <i className="fas fa-image" style={{color: '#4CAF50', fontSize: '14px'}}></i>;
            case 'svg':
                return <i className="fas fa-vector-square" style={{color: '#FF9800', fontSize: '14px'}}></i>;

            // Documents
            case 'md':
                return <i className="fab fa-markdown" style={{color: '#333', fontSize: '14px'}}></i>;
            case 'txt':
                return <i className="fas fa-file-lines" style={{color: '#666', fontSize: '14px'}}></i>;
            case 'pdf':
                return <i className="fas fa-file-pdf" style={{color: '#DC2626', fontSize: '14px'}}></i>;

            // Media
            case 'mp4':
            case 'webm':
            case 'avi':
                return <i className="fas fa-file-video" style={{color: '#9C27B0', fontSize: '14px'}}></i>;
            case 'mp3':
            case 'wav':
            case 'ogg':
                return <i className="fas fa-file-audio" style={{color: '#FF5722', fontSize: '14px'}}></i>;

            // Archives
            case 'zip':
            case 'tar':
            case 'gz':
                return <i className="fas fa-file-zipper" style={{color: '#795548', fontSize: '14px'}}></i>;

            // Asset files (open externally)
            default:
                if (isAssetFile) {
                    return <i className="fas fa-external-link-alt" style={{color: '#2196F3', fontSize: '14px'}}></i>;
                }
                // Default file icon
                return <i className="fas fa-file" style={{color: '#666', fontSize: '14px'}}></i>;
        }
    };

    // Get tooltip text
    const getTooltip = () => {
        let tooltip = node.name;
        if (file?.tooltipSuffix) {
            tooltip += file.tooltipSuffix;
        }
        if (node.isDirectory) {
            tooltip += ' (Right-click to delete)';
        }
        return tooltip;
    };

    // NEW: Handle trash can click separately from folder click
    const handleTrashClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation(); // Prevent folder expansion/contraction

        if (onFolderRightClick && level > 0) {
            onFolderRightClick(node.path, event);
        }
    };

    // Handle click with folder right-click support
    const handleClick = (event: React.MouseEvent) => {
        if (node.isDirectory) {
            if (event.type === 'contextmenu') {
                // Right-click on folder
                event.preventDefault();
                if (onFolderRightClick && level > 0) { // Don't allow deleting root directory
                    onFolderRightClick(node.path, event);
                }
            } else {
                // Left-click on folder - toggle expansion
                setExpanded(!expanded);
            }
        } else {
            // File click
            if (isAssetFile && onAssetClick && file?.assetUrl) {
                onAssetClick(file.assetUrl);
            } else {
                onFileClick(node.path);
            }
        }
    };

    return (
        <div style={{
            overflow: "hidden",
            width: "100%"
        }}>
            {/* FIXED: Separate the folder content from the trash can */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%'
            }}>
                {/* Folder/file content (clickable area) */}
                <div
                    style={{
                        ...getNodeStyle(),
                        flex: 1,
                        marginRight: 0
                    }}
                    onClick={handleClick}
                    onContextMenu={handleClick}
                    title={getTooltip()}
                >
                    {/* Chevron for directories */}
                    <span style={{
                        marginRight: '4px',
                        width: '16px',
                        display: 'inline-block',
                        textAlign: 'center',
                        flexShrink: 0
                    }}>
                {node.isDirectory && (
                    <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`}
                       style={{fontSize: '10px', color: '#666'}}></i>
                )}
            </span>

                    {/* File/folder icon */}
                    <span
                        key={iconKey}
                        style={{
                            marginRight: '8px',
                            width: '16px',
                            display: 'inline-block',
                            textAlign: 'center',
                            flexShrink: 0
                        }}
                    >
                {getIcon()}
            </span>

                    {/* File name with smart truncation */}
                    <span
                        ref={textContainerRef}
                        style={{
                            fontStyle: isAssetFile ? 'italic' : 'normal',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            maxWidth: 'none' // Let our calculation handle the width
                        }}
                        title={node.name} // Always show full name on hover
                    >
                {displayName}
            </span>
                </div>

                {/* FIXED: Trash can completely separate from folder click area */}
                {node.isDirectory && level > 0 && (
                    <span
                        style={{
                            marginLeft: '8px',
                            marginRight: '8px',
                            fontSize: '12px',
                            color: '#999',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            flexShrink: 0,
                            cursor: 'pointer',
                            padding: '2px 4px',
                            borderRadius: '3px'
                        }}
                        className="folder-delete-hint"
                        onClick={handleTrashClick}
                        title="Delete folder"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                🗑️
            </span>
                )}
            </div>

            {/* Render children */}
            {node.isDirectory && expanded && visibleChildren.length > 0 && (
                <div>
                    {visibleChildren.map((childNode, idx) => (
                        <FileTreeView
                            key={`${childNode.path}-${idx}`}
                            node={childNode}
                            level={level + 1}
                            onFileClick={onFileClick}
                            onAssetClick={onAssetClick}
                            onFolderRightClick={onFolderRightClick}
                            activePath={activePath}
                            activeType={activeType}
                            showFileSystemIndicators={showFileSystemIndicators}
                            containerWidth={containerWidth}
                        />
                    ))}
                </div>
            )}

            {/* Add CSS for hover effect */}
            <style dangerouslySetInnerHTML={{
                __html: `
                    .folder-delete-hint {
                        opacity: 0 !important;
                        transition: opacity 0.2s;
                    }
                    div:hover .folder-delete-hint {
                        opacity: 1 !important;
                    }
                `
            }}/>
        </div>
    );
};